import { FC } from 'react';
import { Button, Col, Row, Space } from 'antd';
import { TextQuote } from 'lucide-react';
import { TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownButton, getAdvancedFilterCounts, type AdvancedFilterDefinition } from '@/shared';

import FilterDropdown, { FilterableColumn, ListFilterRule } from '../01-FilterDropdown';
import FilterButtonItem from './FilterButtonItem';

interface AppliedFiltersProps {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    advancedFilters?: AdvancedFilterDefinition<ListFilterRule>[];
    columns?: FilterableColumn[];
    docsLabel?: string;
    filters: ListFilterRule[];
    onAdvancedFilterChange?: (definition: AdvancedFilterDefinition<ListFilterRule> | undefined) => void;
    onAdvancedFiltersChange?: (definitions: AdvancedFilterDefinition<ListFilterRule>[]) => void;
    setFilters: (next: ListFilterRule[] | null) => void;
}

const AppliedFilters: FC<AppliedFiltersProps> = ({
    advancedFilter,
    advancedFilters = [],
    columns = [],
    docsLabel,
    filters,
    onAdvancedFilterChange,
    onAdvancedFiltersChange,
    setFilters,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const resolvedAdvancedFilters = advancedFilters.length ? advancedFilters : advancedFilter ? [advancedFilter] : [];
    const updateAdvancedFilters = (definitions: AdvancedFilterDefinition<ListFilterRule>[]) => {
        if (onAdvancedFiltersChange) {
            onAdvancedFiltersChange(definitions);
        } else {
            onAdvancedFilterChange?.(definitions[0]);
        }
    };

    return (
        <Row align='middle' wrap={false} gutter={[6, 0]}>
            {onAdvancedFiltersChange || onAdvancedFilterChange
                ? resolvedAdvancedFilters.map((resolvedAdvancedFilter, advancedFilterIndex) => {
                      const counts = getAdvancedFilterCounts(resolvedAdvancedFilter);

                      return counts.conditions > 0 ? (
                          <Col
                              key={`${resolvedAdvancedFilter.groups[0]?.id ?? 'advanced-filter'}-${advancedFilterIndex}`}
                              flex='none'
                          >
                              <Space.Compact>
                                  <FilterDropdown
                                      advancedFilter={resolvedAdvancedFilter}
                                      columns={columns}
                                      docsLabel={docsLabel}
                                      filters={filters}
                                      onAdvancedFilterChange={(definition) => {
                                          if (!definition) return;
                                          const next = [...resolvedAdvancedFilters];
                                          next[advancedFilterIndex] = definition;
                                          updateAdvancedFilters(next);
                                      }}
                                      openToAdvancedFilters
                                      setFilters={setFilters}
                                      trigger={
                                          <DropdownButton size='small' type='dashed'>
                                              <TextQuote size='0.8125rem' />
                                              Advanced Filter · {counts.groups}{' '}
                                              {counts.groups === 1 ? 'Group' : 'Groups'} · {counts.conditions}{' '}
                                              {counts.conditions === 1 ? 'Condition' : 'Conditions'}
                                          </DropdownButton>
                                      }
                                  />
                                  <Button
                                      aria-label='Clear advanced filter'
                                      icon={<TbX style={{ color: token.colorTextTertiary }} />}
                                      size='small'
                                      type='dashed'
                                      onClick={() =>
                                          updateAdvancedFilters(
                                              resolvedAdvancedFilters.filter(
                                                  (_, index) => index !== advancedFilterIndex,
                                              ),
                                          )
                                      }
                                  />
                              </Space.Compact>
                          </Col>
                      ) : null;
                  })
                : null}
            {filters.map((filter) => (
                <Col key={filter.id} flex='none'>
                    <FilterButtonItem
                        columns={columns}
                        docsLabel={docsLabel}
                        filter={filter}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Col>
            ))}
        </Row>
    );
};

export default AppliedFilters;
